//Types
import type { CuryProfile } from 'src/types/Cury';

/**
 * Predefined collection of Cury device profiles for different environments and use cases.
 *
 * This object contains a comprehensive set of pre-configured profiles for various
 * home and office environments. Each profile includes optimized scheduling patterns,
 * intensity levels, and operational settings tailored for specific use cases.
 *
 * @type {Record<string, CuryProfile>}
 *
 * @property {CuryProfile} bedroom - Home profile optimized for bedroom use with morning and evening schedules
 * @property {CuryProfile} living_room - Home profile for living room with extended daytime coverage
 * @property {CuryProfile} lavatory_room - Home profile for bathroom with overnight and morning schedules
 * @property {CuryProfile} reception - Office profile for reception areas with business hours coverage
 * @property {CuryProfile} hall - Office profile for hallways with high-intensity morning coverage
 * @property {CuryProfile} workplace - Office profile for work areas with balanced daytime scheduling
 *
 * @example
 * // Access a specific profile
 * const bedroomProfile = profiles.bedroom;
 * console.log(bedroomProfile.label); // 'Bedroom'
 *
 * @example
 * // Iterate through all profiles
 * Object.entries(profiles).forEach(([key, profile]) => {
 *   console.log(`${key}: ${profile.label} (${profile.category})`);
 * });
 *
 */
export const profiles: Record<string, CuryProfile> = {
	// Home Profiles
	bedroom: {
		mode_id: 'bedroom',
		label: 'Bedroom',
		category: 'home',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
			hours: [
				{ time: '06:00-10:00', slot: 'left', intensity: 55 },
				{ time: '10:00-20:00', slot: 'right', intensity: 40 },
				{ time: '20:00-22:00', slot: 'left', intensity: 50 },
			],
		},
	},
	living_room: {
		mode_id: 'living_room',
		label: 'Living Room',
		category: 'home',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
			hours: [
				{ time: '06:00-10:00', slot: 'left', intensity: 70 },
				{ time: '10:00-19:00', slot: 'right', intensity: 45 },
				{ time: '19:00-23:00', slot: 'left', intensity: 60 },
			],
		},
	},
	lavatory_room: {
		mode_id: 'lavatory_room',
		label: 'Lavatory Room',
		category: 'home',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
			hours: [
				{ time: '20:00-07:00', slot: 'right', intensity: 25 },
				{ time: '07:00-09:00', slot: 'left', intensity: 70 },
				{ time: '09:00-17:00', slot: 'right', intensity: 25 },
				{ time: '17:00-20:00', slot: 'left', intensity: 70 },
			],
		},
	},

	// Office Profiles
	reception: {
		mode_id: 'reception',
		label: 'Reception',
		category: 'office',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
			hours: [
				{ time: '07:30-10:00', slot: 'left', intensity: 80 },
				{ time: '10:00-12:00', slot: 'right', intensity: 60 },
				{ time: '12:00-14:00', slot: 'left', intensity: 80 },
				{ time: '14:00-18:30', slot: 'right', intensity: 60 },
			],
		},
	},
	hall: {
		mode_id: 'hall',
		label: 'Hall',
		category: 'office',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
			hours: [
				{ time: '07:00-10:00', slot: 'left', intensity: 90 },
				{ time: '10:00-16:00', slot: 'right', intensity: 50 },
				{ time: '16:00-18:30', slot: 'left', intensity: 80 },
			],
		},
	},
	workplace: {
		mode_id: 'workplace',
		label: 'Workplace',
		category: 'office',
		auto_switching: true,
		schedule: {
			days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
			hours: [
				{ time: '07:30-10:00', slot: 'left', intensity: 70 },
				{ time: '10:00-12:30', slot: 'right', intensity: 40 },
				{ time: '12:30-14:00', slot: 'left', intensity: 70 },
				{ time: '14:00-18:30', slot: 'right', intensity: 40 },
			],
		},
	},
};
